import React, { Key, useEffect, useState } from 'react'
import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import { Link, useNavigate } from 'react-router-dom'
import './UsersList.scss'
import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/filter.svg'
import exportIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/export.svg'
import { checkSearchInputType } from 'Helpers'
import { userBXService } from '../../../../../../BoostXpressAdmin/Services/userBXService'
import { userXModel } from 'BoostXpressAdmin/Model/userXModel'
import { HorizontalLoader } from 'Components'
import { formatString } from 'Helpers/formatString'

const UsersList = () => {
  const navigate = useNavigate()

  const {
    page: pageNumber,
    setPage: setPageNumber,
    limit: pageSize,
    changePageSize,
  } = useListPagination()
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchEndpoint, setSearchEndpoint] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [customers, setCustomers] = useState<userXModel[]>([])
  const [total, setTotal] = useState(0)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  // Filter states
  const [showFilters, setShowFilters] = useState(false)
  const [disabled, setDisabled] = useState('')
  const [emailVerified, setEmailVerified] = useState('')
  const [country, setCountry] = useState('')
  const [userSegment, setUserSegment] = useState('')
  const [phoneVerified, setPhoneVerified] = useState('')
  const [accountType, setAccountType] = useState('')

  const status = 'ALL'

  useEffect(() => {
    // const t = window.setTimeout(() => {
    const trimmed = searchTerm.trim()
    if (!trimmed) {
      setSearchEndpoint('')
      setPageNumber(1)
      return
    }

    const type = checkSearchInputType(trimmed)

    let endpoint = ''
    if (type === 'Email') {
      endpoint = `email=${trimmed}`
    } else if (type === 'PhoneNumber') {
      endpoint = `phone=${trimmed}`
    } else if (type === 'AlphaNumeric') {
      endpoint = `customerAccountId=${encodeURIComponent(trimmed)}`
    } else if (type === 'Numeric') {
      endpoint = `customerUserId=${encodeURIComponent(trimmed)}`
    } else if (type === 'name') {
      endpoint = `search=${encodeURIComponent(trimmed)}`
    } else {
      endpoint = ''
    }

    setSearchEndpoint(endpoint)
    setPageNumber(1)
    // }, 350)

    // return () => window.clearTimeout(t)
  }, [searchTerm])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setIsLoading(true)

        // Build filter parameters
        const params: any = {
          page: pageNumber,
          limit: pageSize,
          sortOrder: sortOrder,
        }

        // Add search endpoint if exists
        if (searchEndpoint) {
          const searchParams = searchEndpoint.split('&')
          searchParams.forEach((param) => {
            const [key, value] = param.split('=')
            params[key] = value
          })
        }

        // Add filters
        if (disabled) params.disabled = disabled
        if (emailVerified) params.emailVerified = emailVerified
        if (country) params.country = country
        if (userSegment) params.userSegment = userSegment
        if (phoneVerified) params.phoneVerified = phoneVerified
        if (accountType) params.accountType = accountType

        const res = await userBXService.index(pageNumber, pageSize, sortOrder, params)
        if (cancelled) return
        setCustomers(res.data?.data ?? [])
        setTotal(res.data?.meta?.pagination?.total ?? 0)
      } catch (e) {
        if (!cancelled) {
          setCustomers([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [
    pageNumber,
    pageSize,
    searchEndpoint,
    sortOrder,
    disabled,
    emailVerified,
    country,
    userSegment,
    phoneVerified,
    accountType,
  ])

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-user-menu-root="true"]')) return
      setOpenMenuId(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  const handleExport = async () => {
    try {
      setIsExporting(true)

      const params: any = {
        sortOrder: sortOrder,
      }

      if (searchEndpoint) {
        const searchParams = searchEndpoint.split('&')
        searchParams.forEach((param) => {
          const [key, value] = param.split('=')
          params[key] = value
        })
      }

      if (disabled) params.disabled = disabled
      if (emailVerified) params.emailVerified = emailVerified
      if (country) params.country = country
      if (userSegment) params.userSegment = userSegment
      if (phoneVerified) params.phoneVerified = phoneVerified
      if (accountType) params.accountType = accountType

      const response = await userBXService.csvExport(params)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `users-export-${new Date().getTime()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className='bx-users-list'>
      <h2 className='bx-users-list__title'>Users Management</h2>

      <div className='bx-users-list__container'>
        <div className='bx-users-list__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-8 col-xxl-10'>
            <div className='bx-search-wrapper w-100'>
              <div className='bx-search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                placeholder='Search by name, msisdn, email'
                // , email, phone number or user ID'
                className='bx-search-input'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className='col-6 col-md-2 col-xxl-1 position-relative'>
            <button className='bx-action-btn w-100' onClick={() => setShowFilters(!showFilters)}>
              <img src={filterIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Filters</span>
            </button>

            {showFilters && (
              <div className='bx-users-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Users</h5>
                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Account Status</label>
                    <select
                      value={disabled}
                      onChange={(e) => setDisabled(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Users</option>
                      <option value='true'>Disabled</option>
                      <option value='false'>Active</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Email Verification</label>
                    <select
                      value={emailVerified}
                      onChange={(e) => setEmailVerified(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Users</option>
                      <option value='true'>Verified</option>
                      <option value='false'>Not Verified</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Phone Verification</label>
                    <select
                      value={phoneVerified}
                      onChange={(e) => setPhoneVerified(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Users</option>
                      <option value='true'>Verified</option>
                      <option value='false'>Not Verified</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Account Type</label>
                    <select
                      value={accountType}
                      onChange={(e) => setAccountType(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Types</option>
                      <option value='CUSTOMER'>Customer</option>
                      <option value='STATION_ATTENDANT'>Station Attendant</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>User Group</label>
                    <select
                      value={userSegment}
                      onChange={(e) => setUserSegment(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Groups</option>
                      <option value='unfunded_dormant'>Unfunded Dormant</option>
                      <option value='depositors'>Depositors</option>
                      <option value='trialists'>Trialists</option>
                      <option value='micro_transactors'>Micro Transactors</option>
                      <option value='mid_tier'>Mid Tier</option>
                      <option value='power_users'>Power Users</option>
                      <option value='recently_active'>Recently Active</option>
                      <option value='churned'>Churned</option>
                      <option value='voucher_users'>Voucher Users</option>
                      <option value='referrers'>Referrers</option>
                      <option value='phone_unverified'>Phone Unverified</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Country</label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Countries</option>
                      <option value='Nigeria'>Nigeria</option>
                    </select>
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    className='btn btn-secondary'
                    onClick={() => {
                      setDisabled('')
                      setEmailVerified('')
                      setCountry('')
                      setUserSegment('')
                      setPhoneVerified('')
                      setAccountType('')
                    }}
                  >
                    Clear Filters
                  </button>
                  <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className='col-6 col-md-2 col-xxl-1'>
            <button
              className='bx-action-btn w-100'
              onClick={handleExport}
              disabled={isExporting || isLoading}
            >
              <img src={exportIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>{isExporting ? 'Exporting...' : 'Export'}</span>
            </button>
          </div>
        </div>

        <div className='bx-table-wrapper'>
          <table className='bx-table'>
            <thead>
              <tr>
                <th className='bx-th-name'>S/N</th>
                <th className='bx-th-name'>Name</th>
                <th className='bx-th-email'>Email</th>
                <th className='bx-th-phone'>Phone</th>
                <th className='bx-th-phone'>Type</th>
                {/* <th className='bx-th-status'>Status</th> */}
                <th className='bx-th-action'>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className='bx-td-name'>
                    <HorizontalLoader></HorizontalLoader>
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={8} className='bx-td-name'>
                    No users found
                  </td>
                </tr>
              ) : (
                customers.map((c: userXModel, index: Key) => {
                  const fullName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()

                  return (
                    <tr key={c.resourceId}>
                      <td className='bx-td-name'>
                        {Number(index) + 1 + (pageNumber - 1) * pageSize}
                      </td>
                      <td className='bx-td-name'>{formatString(fullName)}</td>
                      <td className='bx-td-email'>{c.email || '-'}</td>
                      <td className='bx-td-phone'>{c.phone || '-'}</td>
                      <td className='bx-td-type'>{formatString(c.accountType) || '-'}</td>
                      {/* <td className='bx-td-status'>
                        <div className='bx-badge-wrapper'>
                          <span className={`bx-badge bx-badge--${status.toLowerCase()}`}>
                            {status}
                          </span>
                        </div>
                      </td> */}
                      <td className='bx-td-action'>
                        <div className='bx-user-table__menu' data-user-menu-root='true'>
                          <button
                            type='button'
                            className='bx-user-table__menu-btn'
                            aria-haspopup='menu'
                            aria-expanded={openMenuId === c.resourceId}
                            onClick={() =>
                              setOpenMenuId((v) => (v === c.resourceId ? null : c.resourceId))
                            }
                          >
                            <svg
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <circle cx='10' cy='4.5' r='1.3' fill='#667085' />
                              <circle cx='10' cy='10' r='1.3' fill='#667085' />
                              <circle cx='10' cy='15.5' r='1.3' fill='#667085' />
                            </svg>
                          </button>

                          {openMenuId === c.resourceId ? (
                            <div className='bx-user-table__menu-dropdown' role='menu'>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  navigate(`/boostxpress/users/${encodeURIComponent(c.resourceId)}`)
                                }}
                              >
                                View
                              </button>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  navigate(
                                    `/boostxpress/transactions?userResourceId=${encodeURIComponent(c.resourceId)}`,
                                  )
                                }}
                              >
                                Transactions
                              </button>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  navigate(
                                    `/boostxpress/orders?userResourceId=${encodeURIComponent(c.resourceId)}`,
                                  )
                                }}
                              >
                                Orders
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          page={pageNumber}
          limit={pageSize}
          total={total}
          disabled={isLoading}
          onPageChange={setPageNumber}
          onPageSizeChange={changePageSize}
        />
      </div>
    </div>
  )
}

export default UsersList
